import { useCallback, useEffect, useRef, useState } from "react";
import JSZip from "jszip";
import { staffChatImportApi } from "@/api";
import type {
  ChatAttachment,
  ChatImportCommitResult,
  ChatImportMessageInput,
  ChatImportParticipants,
  ChatImportRun,
  JobListRow,
} from "@/types";
import { Button, Spinner } from "@/components/ui";
import { JobPicker } from "@/staff/review/JobPicker";
import { useChatFeed } from "./ChatContext";
import { formatDate, formatDateTime } from "@/lib/format";
import "@/staff/review/review.css";
import "./chat.css";

/**
 * Chat › Import History — the Moxo → portal migration utility.
 *
 * Takes one export zip per conversation (Claude Cowork extraction format:
 * conversation.json + transcript.txt + files/*), parses it in the browser,
 * lets the operator pick the job whose thread receives the history and map
 * each Moxo sender name onto a real staff user or portal user, then uploads
 * the inline files and commits the whole thread in one transaction. The
 * server stores every message at its ORIGINAL timestamp, born read on both
 * sides, with no push fan-out — so imported history slots silently under any
 * live conversation.
 *
 * Update mode: re-attaching a later export of a board already imported into
 * the SAME job diffs it against the recorded high-water mark (Moxo seq, with
 * a timestamp fallback) and imports only the newer entries — clients can keep
 * chatting on Moxo mid-migration and the thread is topped up, never
 * duplicated. A board known only on a different job still gets the
 * full-duplicate warning + explicit override.
 *
 * Clients not yet on the portal: the job's clients WITHOUT a portal_user are
 * offered as "create portal access" mappings — importing provisions their
 * record through POST portal/invite with send_invite_email=false (the same
 * record the real invite flow makes; nothing is emailed, and the client can't
 * sign in until they request their own magic link). The proper invite goes
 * out later from the Client page, once the imported history has been checked.
 */

/** One importable item — a Moxo chat message, or a shared file (which becomes
 *  an attachment-only message at its own timestamp). */
interface ParsedItem {
  at: number;
  seq: number;
  sender: string;
  body: string;
  file?: { name: string; mime: string; size: number; savedAs: string };
}

interface ParsedExport {
  boardId: string;
  boardName: string;
  items: ParsedItem[];
  /** Distinct sender names in first-appearance order. */
  senders: string[];
  fileCount: number;
  firstAt: number;
  lastAt: number;
  /** Chat messages with no text and no file, dropped at parse time. */
  skipped: number;
  zip: JSZip;
}

/** "" = unmapped; otherwise "staff:<user_id>", "client:<portal_user_id>",
 *  "newclient:<client_id>" — a job client with no portal record yet, whose
 *  portal_user is created at import time (no invite email is sent) — or, since
 *  the builders build, "builder:<portal_user_id>" (a builder login on this
 *  job) / "newbuilder:<builder_id>" (an attached firm with no login yet, whose
 *  login is created silently the same way). */
type MappingValue = string;

interface Progress {
  stage: "accounts" | "files" | "commit";
  done: number;
  total: number;
}

/** Moxo timestamps are ISO strings; the API wants epoch ms. */
function toEpoch(iso: unknown): number {
  if (typeof iso !== "string") return 0;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : 0;
}

function normName(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

async function parseExportZip(file: File): Promise<ParsedExport> {
  const zip = await JSZip.loadAsync(file);
  const conv = zip.file("conversation.json");
  if (!conv) {
    throw new Error("No conversation.json in the zip — is this a Moxo export?");
  }
  let data: {
    boardId?: unknown;
    name?: unknown;
    messages?: {
      seq?: number;
      at?: string;
      from?: string;
      text?: string | null;
      link?: string | null;
    }[];
    files?: {
      seq?: number;
      at?: string;
      by?: string;
      name?: string;
      contentType?: string;
      bytes?: number;
      savedAs?: string;
    }[];
  };
  try {
    data = JSON.parse(await conv.async("string"));
  } catch {
    throw new Error("conversation.json is not valid JSON.");
  }
  const boardId = typeof data.boardId === "string" ? data.boardId : "";
  const boardName = typeof data.name === "string" ? data.name : "";
  if (!boardId) throw new Error("conversation.json has no boardId.");

  const items: ParsedItem[] = [];
  let skipped = 0;
  for (const m of data.messages ?? []) {
    const at = toEpoch(m.at);
    const text = (m.text ?? "").trim();
    const body = m.link ? (text ? `${text}\n${m.link}` : m.link) : text;
    if (!body) {
      skipped += 1;
      continue;
    }
    items.push({ at, seq: m.seq ?? 0, sender: (m.from ?? "").trim() || "Unknown", body });
  }
  const missing: string[] = [];
  for (const f of data.files ?? []) {
    const savedAs = f.savedAs ?? "";
    if (!savedAs || !zip.file(savedAs)) {
      missing.push(f.name || savedAs || "unnamed file");
      continue;
    }
    items.push({
      at: toEpoch(f.at),
      seq: f.seq ?? 0,
      sender: (f.by ?? "").trim() || "Unknown",
      body: "",
      file: {
        name: f.name || savedAs.split("/").pop() || "attachment",
        mime: f.contentType || "application/octet-stream",
        size: f.bytes ?? 0,
        savedAs,
      },
    });
  }
  if (missing.length > 0) {
    throw new Error(`The zip is missing ${missing.length} file(s) it lists: ${missing.join(", ")}`);
  }
  if (items.length === 0) {
    throw new Error("This export holds no importable messages or files.");
  }
  if (items.some((i) => i.at <= 0)) {
    throw new Error("Some entries have unreadable timestamps — re-extract the conversation.");
  }
  items.sort((a, b) => a.at - b.at || a.seq - b.seq);

  const senders: string[] = [];
  for (const i of items) {
    if (!senders.includes(i.sender)) senders.push(i.sender);
  }
  return {
    boardId,
    boardName,
    items,
    senders,
    fileCount: items.filter((i) => i.file).length,
    firstAt: items[0]!.at,
    lastAt: items[items.length - 1]!.at,
    skipped,
    zip,
  };
}

export function ImportHistoryPage() {
  const { refresh: refreshChatFeed } = useChatFeed();

  // Run log (also the global double-import check).
  const [runs, setRuns] = useState<ChatImportRun[] | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);

  // The import being assembled.
  const [job, setJob] = useState<JobListRow | null>(null);
  const [parsed, setParsed] = useState<ParsedExport | null>(null);
  const [zipName, setZipName] = useState("");
  const [parseError, setParseError] = useState<string | null>(null);
  const [participants, setParticipants] = useState<ChatImportParticipants | null>(null);
  const [participantsError, setParticipantsError] = useState<string | null>(null);
  const [mapping, setMapping] = useState<Record<string, MappingValue>>({});
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [result, setResult] = useState<ChatImportCommitResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Uploads that already succeeded, keyed by savedAs — a commit retry after a
  // failure must not re-upload bytes (the staged rows are still valid).
  const uploadedRef = useRef<Map<string, ChatAttachment>>(new Map());
  // Portal records already created this run, keyed by client_id — the silent
  // invite is idempotent anyway, but a retry shouldn't repeat the calls.
  const createdRef = useRef<Map<number, number>>(new Map());
  // Same, for builder logins created this run, keyed by builder_id.
  const createdBuilderRef = useRef<Map<number, number>>(new Map());
  // Bumped after a successful import so the palette reflects freshly created
  // portal users on the next run for the same job.
  const [participantsVersion, setParticipantsVersion] = useState(0);

  const loadRuns = useCallback(() => {
    setRunsError(null);
    staffChatImportApi
      .listImports()
      .then(setRuns)
      .catch((err: unknown) => {
        setRunsError(err instanceof Error ? err.message : "Could not load previous imports.");
        setRuns([]);
      });
  }, []);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Load the mapping palette whenever the job changes.
  useEffect(() => {
    if (!job) {
      setParticipants(null);
      return;
    }
    let cancelled = false;
    setParticipants(null);
    setParticipantsError(null);
    staffChatImportApi
      .getImportParticipants(job.id)
      .then((p) => {
        if (!cancelled) setParticipants(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setParticipantsError(
            err instanceof Error ? err.message : "Could not load the job's participants.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [job, participantsVersion]);

  // Auto-match sender names once both the export and the palette are in.
  useEffect(() => {
    if (!parsed || !participants) return;
    setMapping((prev) => {
      const next = { ...prev };
      for (const sender of parsed.senders) {
        if (next[sender]) continue;
        const n = normName(sender);
        const staffHit = participants.staff.find((s) => normName(s.name) === n);
        if (staffHit) {
          next[sender] = `staff:${staffHit.user_id}`;
          continue;
        }
        const clientHit = participants.clients.find((c) => normName(c.name) === n);
        if (clientHit) {
          next[sender] = `client:${clientHit.portal_user_id}`;
          continue;
        }
        const creatableHit = participants.clients_without_portal.find(
          (c) => c.has_email && normName(c.name) === n,
        );
        if (creatableHit) {
          next[sender] = `newclient:${creatableHit.client_id}`;
          continue;
        }
        const builderHit = (participants.builders ?? []).find((b) => normName(b.name) === n);
        if (builderHit) {
          next[sender] = `builder:${builderHit.portal_user_id}`;
          continue;
        }
        const creatableBuilder = (participants.builders_without_portal ?? []).find(
          (b) => b.has_email && (normName(b.contact_name) === n || normName(b.firm) === n),
        );
        if (creatableBuilder) next[sender] = `newbuilder:${creatableBuilder.builder_id}`;
      }
      return next;
    });
  }, [parsed, participants]);

  function onPickZip(file: File | undefined) {
    if (!file) return;
    setParsed(null);
    setParseError(null);
    setResult(null);
    setImportError(null);
    setMapping({});
    setAllowDuplicate(false);
    uploadedRef.current.clear();
    createdRef.current.clear();
    createdBuilderRef.current.clear();
    setZipName(file.name);
    parseExportZip(file)
      .then(setParsed)
      .catch((err: unknown) => {
        setParseError(err instanceof Error ? err.message : "Could not read the zip.");
      });
  }

  function changeJob() {
    setJob(null);
    setMapping({});
    setResult(null);
    setImportError(null);
    uploadedRef.current.clear();
    createdRef.current.clear();
    createdBuilderRef.current.clear();
  }

  // ── Mode derivation ──────────────────────────────────────────────────
  // Same board already imported into THIS job → update mode: only entries past
  // the recorded high-water mark (Moxo seq; timestamp fallback for runs that
  // predate last_seq) are imported, appending to the existing thread. Same
  // board known only on OTHER jobs → the full-duplicate warning + override.
  const sameJobRuns =
    parsed && participants
      ? participants.prior_imports.filter((p) => p.board_id === parsed.boardId)
      : [];
  const updateMode = sameJobRuns.length > 0;
  const highSeq = Math.max(0, ...sameJobRuns.map((p) => p.last_seq));
  const highAt = Math.max(0, ...sameJobRuns.map((p) => p.last_message_at));
  const priorAnywhere = parsed
    ? (runs ?? []).find((r) => r.board_id === parsed.boardId)
    : undefined;
  const itemsToImport = parsed
    ? updateMode
      ? parsed.items.filter((i) => (highSeq > 0 ? i.seq > highSeq : i.at > highAt))
      : parsed.items
    : [];
  const fileCountToImport = itemsToImport.filter((i) => i.file).length;
  const sendersToMap: string[] = [];
  for (const i of itemsToImport) {
    if (!sendersToMap.includes(i.sender)) sendersToMap.push(i.sender);
  }
  const allMapped = itemsToImport.length > 0 && sendersToMap.every((s) => !!mapping[s]);
  const canImport =
    !!job &&
    !!parsed &&
    !!participants &&
    allMapped &&
    !progress &&
    (updateMode || !priorAnywhere || allowDuplicate);

  async function runImport() {
    if (!job || !parsed || !canImport) return;
    setImportError(null);
    setResult(null);
    try {
      // 0. Create portal records for senders mapped to clients without one —
      //    POST portal/invite with send_invite_email=false: same record the
      //    real invite flow would make, nothing emailed. Idempotent, and
      //    cached so a retry doesn't repeat the calls.
      const newClientIds: number[] = [];
      const newBuilderIds: number[] = [];
      for (const sender of sendersToMap) {
        const value = mapping[sender]!;
        if (value.startsWith("newclient:")) {
          const cid = Number(value.slice("newclient:".length));
          if (!newClientIds.includes(cid)) newClientIds.push(cid);
        } else if (value.startsWith("newbuilder:")) {
          const bid = Number(value.slice("newbuilder:".length));
          if (!newBuilderIds.includes(bid)) newBuilderIds.push(bid);
        }
      }
      const portalIdByClient = createdRef.current;
      const portalIdByBuilder = createdBuilderRef.current;
      const accountsTotal = newClientIds.length + newBuilderIds.length;
      if (accountsTotal > 0) {
        setProgress({ stage: "accounts", done: 0, total: accountsTotal });
        let doneAccounts = 0;
        for (const cid of newClientIds) {
          if (!portalIdByClient.has(cid)) {
            const created = await staffChatImportApi.createPortalAccessSilently(cid);
            portalIdByClient.set(cid, created.portal_user_id);
          }
          doneAccounts += 1;
          setProgress({ stage: "accounts", done: doneAccounts, total: accountsTotal });
        }
        for (const bid of newBuilderIds) {
          if (!portalIdByBuilder.has(bid)) {
            const firm = (participants?.builders_without_portal ?? []).find((b) => b.builder_id === bid);
            if (!firm) throw new Error("A builder mapping no longer matches the job — reload and try again.");
            const created = await staffChatImportApi.createBuilderAccessSilently(
              bid,
              firm.contact_name || firm.firm,
              firm.email,
            );
            portalIdByBuilder.set(bid, created.portal_user_id);
          }
          doneAccounts += 1;
          setProgress({ stage: "accounts", done: doneAccounts, total: accountsTotal });
        }
      }

      // 1. Upload the inline files this run needs (sequentially — progress
      //    stays honest; in update mode that's only the new ones).
      const fileItems = itemsToImport.filter((i) => i.file);
      setProgress({ stage: "files", done: 0, total: fileItems.length });
      const attachmentBySavedAs = uploadedRef.current;
      let done = 0;
      for (const item of fileItems) {
        const f = item.file!;
        if (!attachmentBySavedAs.has(f.savedAs)) {
          const entry = parsed.zip.file(f.savedAs)!;
          const buf = await entry.async("arraybuffer");
          const blob = new Blob([buf], { type: f.mime });
          const uploaded = await staffChatImportApi.uploadImportAttachment(job.id, blob, f.name);
          attachmentBySavedAs.set(f.savedAs, uploaded);
        }
        done += 1;
        setProgress({ stage: "files", done, total: fileItems.length });
      }

      // 2. Commit the mapped thread in one transaction.
      setProgress({ stage: "commit", done: fileItems.length, total: fileItems.length });
      const messages: ChatImportMessageInput[] = itemsToImport.map((item) => {
        const value = mapping[item.sender]!;
        const [kind, idText] = value.split(":");
        const id = Number(idText);
        const portalUserId =
          kind === "newclient"
            ? portalIdByClient.get(id)!
            : kind === "newbuilder"
              ? portalIdByBuilder.get(id)!
              : kind === "client" || kind === "builder"
                ? id
                : 0;
        const senderKind: ChatImportMessageInput["sender_kind"] =
          kind === "staff" ? "staff" : kind === "builder" || kind === "newbuilder" ? "builder" : "client";
        return {
          at: item.at,
          sender_kind: senderKind,
          sender_user_id: kind === "staff" ? id : 0,
          sender_portal_user_id: portalUserId,
          body: item.body,
          attachment_ids: item.file
            ? [attachmentBySavedAs.get(item.file.savedAs)!.attachment_id]
            : [],
        };
      });
      const res = await staffChatImportApi.commitImport({
        jobId: job.id,
        boardId: parsed.boardId,
        boardName: parsed.boardName,
        allowDuplicate: updateMode ? false : allowDuplicate,
        append: updateMode,
        lastSeq: Math.max(0, ...itemsToImport.map((i) => i.seq)),
        messages,
      });
      setResult(res);
      setParsed(null);
      setZipName("");
      setMapping({});
      setAllowDuplicate(false);
      uploadedRef.current.clear();
      createdRef.current.clear();
      createdBuilderRef.current.clear();
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadRuns();
      refreshChatFeed();
      setParticipantsVersion((v) => v + 1);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Import failed — nothing was written.");
    } finally {
      setProgress(null);
    }
  }

  return (
    <section className="chat-import">
      <header className="chat-import__head">
        <h1>Import History</h1>
        <p className="chat-import__blurb">
          Bring a Moxo conversation into the new portal: attach the exported zip, choose the job it
          belongs to, match each sender to a real account, and import. Messages keep their original
          timestamps and arrive already read — nothing pings anyone.
        </p>
      </header>

      {result ? (
        <div className="chat-import__success" role="status">
          Imported {result.imported_messages} message{result.imported_messages === 1 ? "" : "s"}
          {result.imported_attachments > 0
            ? ` (${result.imported_attachments} attachment${result.imported_attachments === 1 ? "" : "s"})`
            : ""}{" "}
          spanning {formatDate(result.first_message_at)} – {formatDate(result.last_message_at)}. The
          history now sits in the job's thread, marked as read.
        </div>
      ) : null}

      <div className="chat-import__card">
        <h2 className="chat-import__card-title">Run an import</h2>

        {/* Step 1 — the export zip */}
        <div className="chat-import__step">
          <span className="chat-import__step-label">Conversation export (.zip)</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            className="chat-import__file"
            onChange={(e) => onPickZip(e.target.files?.[0])}
          />
          {parseError ? (
            <p className="chat-import__error" role="alert">
              {parseError}
            </p>
          ) : null}
          {parsed ? (
            <p className="chat-import__summary">
              <strong>{parsed.boardName || zipName}</strong> — {parsed.items.length} entr
              {parsed.items.length === 1 ? "y" : "ies"} ({parsed.fileCount} file
              {parsed.fileCount === 1 ? "" : "s"}), {formatDate(parsed.firstAt)} –{" "}
              {formatDate(parsed.lastAt)}
              {parsed.skipped > 0 ? ` · ${parsed.skipped} empty message(s) skipped` : ""}
            </p>
          ) : null}
          {parsed && priorAnywhere && !job ? (
            <p className="chat-import__hint">
              This conversation was imported before
              {priorAnywhere.job_number ? ` into #${priorAnywhere.job_number}` : ""} on{" "}
              {formatDateTime(priorAnywhere.imported_at)}. Pick that same job below to add just
              its new messages.
            </p>
          ) : null}
        </div>

        {/* Step 2 — the job */}
        <div className="chat-import__step">
          <span className="chat-import__step-label">Job to link it to</span>
          {job ? (
            <p className="chat-import__summary">
              <span className="numeric">#{job.job_number}</span> ·{" "}
              <strong>{job.job_name || "Untitled job"}</strong>
              {job.client_display ? ` — ${job.client_display}` : ""}{" "}
              <button type="button" className="chat-import__link-btn" onClick={changeJob}>
                Change
              </button>
            </p>
          ) : (
            <JobPicker onPick={setJob} triggerLabel="Find a job…" />
          )}
          {job && participants && participants.existing_message_count > 0 ? (
            <p className="chat-import__hint">
              This job's thread already has {participants.existing_message_count} message
              {participants.existing_message_count === 1 ? "" : "s"} — the imported history will
              slot in by date.
            </p>
          ) : null}
          {participantsError ? (
            <p className="chat-import__error" role="alert">
              {participantsError}
            </p>
          ) : null}
        </div>

        {/* Mode banner — what this run does when the board is already known */}
        {parsed && job && participants ? (
          updateMode ? (
            <div className="chat-import__update" role="status">
              {itemsToImport.length > 0 ? (
                <p>
                  This conversation was imported into this job before, up to{" "}
                  {formatDateTime(highAt)}. This zip adds {itemsToImport.length} newer entr
                  {itemsToImport.length === 1 ? "y" : "ies"}
                  {fileCountToImport > 0
                    ? ` including ${fileCountToImport} file${fileCountToImport === 1 ? "" : "s"}`
                    : ""}{" "}
                  — only those will be imported.
                </p>
              ) : (
                <p>
                  This job's thread is already up to date with this zip (imported up to{" "}
                  {formatDateTime(highAt)}) — there is nothing new to add.
                </p>
              )}
            </div>
          ) : priorAnywhere ? (
            <div className="chat-import__warn" role="alert">
              <p>
                This conversation was already imported into{" "}
                {priorAnywhere.job_number ? `#${priorAnywhere.job_number}` : "another job"} on{" "}
                {formatDateTime(priorAnywhere.imported_at)} ({priorAnywhere.message_count}{" "}
                messages). Importing it here makes a full second copy — to add just its new
                messages, pick that job instead.
              </p>
              <label className="chat-import__check">
                <input
                  type="checkbox"
                  checked={allowDuplicate}
                  onChange={(e) => setAllowDuplicate(e.target.checked)}
                />
                Import a full copy anyway
              </label>
            </div>
          ) : null
        ) : null}

        {/* Step 3 — sender mapping */}
        {parsed && job && itemsToImport.length > 0 ? (
          participants ? (
            <div className="chat-import__step">
              <span className="chat-import__step-label">Who's who</span>
              <p className="chat-import__hint">
                Match each name from the Moxo export to the account whose messages they become.
                Matching someone to a client without portal access creates their portal record
                during the import — no invite email is sent; invite them later from their Client
                page when you're ready. Builders work the same way, but the firm must already be
                attached to the job (Clients tab → Builders, with "show the full chat history").
              </p>
              <table className="chat-import__map">
                <tbody>
                  {sendersToMap.map((sender) => (
                    <tr key={sender}>
                      <td className="chat-import__map-name">{sender}</td>
                      <td>
                        <select
                          className="chat-import__select"
                          value={mapping[sender] ?? ""}
                          onChange={(e) =>
                            setMapping((prev) => ({ ...prev, [sender]: e.target.value }))
                          }
                        >
                          <option value="">— choose an account —</option>
                          {participants.staff.length > 0 ? (
                            <optgroup label="Staff">
                              {participants.staff.map((s) => (
                                <option key={`staff:${s.user_id}`} value={`staff:${s.user_id}`}>
                                  {s.name}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                          {participants.clients.length > 0 ? (
                            <optgroup label="Clients on this job">
                              {participants.clients.map((c) => (
                                <option
                                  key={`client:${c.portal_user_id}`}
                                  value={`client:${c.portal_user_id}`}
                                >
                                  {c.name}
                                  {c.email ? ` (${c.email})` : ""}
                                  {c.is_active ? "" : " — deactivated"}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                          {participants.clients_without_portal.length > 0 ? (
                            <optgroup label="Create portal access (no invite sent)">
                              {participants.clients_without_portal.map((c) => (
                                <option
                                  key={`newclient:${c.client_id}`}
                                  value={`newclient:${c.client_id}`}
                                  disabled={!c.has_email}
                                >
                                  {c.name}
                                  {c.has_email
                                    ? ` (${c.email})`
                                    : " — no email on the client record"}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                          {(participants.builders ?? []).length > 0 ? (
                            <optgroup label="Builders on this job">
                              {(participants.builders ?? []).map((b) => (
                                <option
                                  key={`builder:${b.portal_user_id}`}
                                  value={`builder:${b.portal_user_id}`}
                                >
                                  {b.name} — {b.firm}
                                  {b.email ? ` (${b.email})` : ""}
                                  {b.is_active ? "" : " — deactivated"}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                          {(participants.builders_without_portal ?? []).length > 0 ? (
                            <optgroup label="Create builder access (no invite sent)">
                              {(participants.builders_without_portal ?? []).map((b) => (
                                <option
                                  key={`newbuilder:${b.builder_id}`}
                                  value={`newbuilder:${b.builder_id}`}
                                  disabled={!b.has_email}
                                >
                                  {b.contact_name ? `${b.contact_name} — ${b.firm}` : b.firm}
                                  {b.has_email ? ` (${b.email})` : " — no email on the builder record"}
                                </option>
                              ))}
                            </optgroup>
                          ) : null}
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {participants.clients.length === 0 &&
              participants.clients_without_portal.length === 0 ? (
                <p className="chat-import__hint">
                  No clients are attached to this job yet — client messages can't be mapped until
                  the client is attached to the job.
                </p>
              ) : null}
            </div>
          ) : !participantsError ? (
            <Spinner label="Loading participants…" />
          ) : null
        ) : null}

        {/* Step 4 — go */}
        <div className="chat-import__actions">
          <Button onClick={() => void runImport()} disabled={!canImport} loading={!!progress}>
            {progress
              ? progress.stage === "accounts"
                ? `Creating portal records… ${progress.done} of ${progress.total}`
                : progress.stage === "files"
                  ? `Uploading files… ${progress.done} of ${progress.total}`
                  : "Writing messages…"
              : updateMode
                ? itemsToImport.length > 0
                  ? `Import ${itemsToImport.length} new entr${itemsToImport.length === 1 ? "y" : "ies"}`
                  : "Nothing new to import"
                : "Import conversation"}
          </Button>
          {parsed && job && participants && !allMapped && itemsToImport.length > 0 ? (
            <span className="chat-import__hint">Map every sender to enable the import.</span>
          ) : null}
        </div>
        {importError ? (
          <p className="chat-import__error" role="alert">
            {importError}
          </p>
        ) : null}
      </div>

      <div className="chat-import__card">
        <h2 className="chat-import__card-title">Previous imports</h2>
        {runsError ? (
          <p className="chat-import__error" role="alert">
            {runsError}
          </p>
        ) : null}
        {runs === null ? (
          <Spinner label="Loading previous imports…" />
        ) : runs.length === 0 ? (
          <p className="chat-import__hint">No conversations have been imported yet.</p>
        ) : (
          <table className="chat-import__runs">
            <thead>
              <tr>
                <th>Imported</th>
                <th>Conversation</th>
                <th>Job</th>
                <th>Messages</th>
                <th>Files</th>
                <th>Covers</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td>{formatDateTime(r.imported_at)}</td>
                  <td>{r.board_name || r.board_id}</td>
                  <td>
                    {r.job_number ? <span className="numeric">#{r.job_number} </span> : null}
                    {r.job_name || "—"}
                  </td>
                  <td className="numeric">{r.message_count}</td>
                  <td className="numeric">{r.attachment_count}</td>
                  <td>
                    {formatDate(r.first_message_at)} – {formatDate(r.last_message_at)}
                  </td>
                  <td>{r.imported_by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
